// client/src/components/publishing/AiCaptionAssistant.jsx
//
// AI caption assistant for the post composers (Roadmap Phase 3.2): draft a
// caption from a short brief, shorten the current text, adapt it to one
// platform, or suggest hashtags. Nothing changes the post until the user
// picks "Use this" / a hashtag. Hidden entirely when the assistant is off on
// the server (feature flag / no provider) or the database isn't updated.
// Each answer counts against the plan's monthly AI allowance.
//
// API: /api/publishing/:tenant/ai/* (server/routes/publishingRoutes.js).

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaMagic } from 'react-icons/fa';
import { getAiStatus, aiCaption, errorText } from '../../services/publishingApi';
import './Publishing.css';

// Same limits as server/config/publishing.js; used to pick the strictest platform.
const LIMITS = { twitter: 280, bluesky: 300, threads: 500, instagram: 2200, tiktok: 2200, linkedin: 3000, youtube: 5000, facebook: 63206, reddit: 40000 };
const NAMES = { twitter: 'X (Twitter)', bluesky: 'Bluesky', threads: 'Threads', instagram: 'Instagram', tiktok: 'TikTok', linkedin: 'LinkedIn', youtube: 'YouTube', facebook: 'Facebook', reddit: 'Reddit' };

export default function AiCaptionAssistant({ tenantId, text, onChangeText, platforms = [], eventId = null }) {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [brief, setBrief] = useState('');
  const [tone, setTone] = useState('friendly');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    if (tenantId) getAiStatus(tenantId).then((s) => { if (live) setStatus(s); }).catch(() => { if (live) setStatus(null); });
    return () => { live = false; };
  }, [tenantId]);

  const known = useMemo(() => platforms.filter((p) => LIMITS[p]), [platforms]);
  const strictest = useMemo(() => known.slice().sort((a, b) => LIMITS[a] - LIMITS[b])[0] || '', [known]);
  const platform = known.includes(target) ? target : strictest;

  if (!status || !status.enabled) return null;
  const usage = status.usage || {};
  const outOfAllowance = !usage.unlimited && usage.limit != null && usage.used >= usage.limit;

  const run = async (action) => {
    setBusy(action);
    setError(null);
    setResult(null);
    try {
      const r = await aiCaption(tenantId, {
        action,
        text: text || '',
        brief: action === 'draft' ? brief : undefined,
        platform: platform || undefined,
        tone,
        language: i18n.language,
        eventId: eventId || undefined,
      });
      setResult(r);
      if (r.usage) setStatus((s) => ({ ...s, usage: { ...s.usage, ...r.usage } }));
    } catch (err) {
      setError({ code: err?.response?.data?.code, message: errorText(err, t('publishing.ai.error', 'The assistant could not answer. Try again.')) });
    } finally {
      setBusy('');
    }
  };

  const addTag = (tag) => {
    const current = text || '';
    if (current.includes(tag)) return;
    onChangeText(`${current}${current && !/\s$/.test(current) ? ' ' : ''}${tag}`);
  };

  return (
    <div className="pb-ai" data-testid="ai-assistant">
      <button type="button" className="pb-ai-toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <FaMagic aria-hidden="true" /> {t('publishing.ai.title', 'AI caption assistant')}
        <span className="pb-ai-usage">
          {usage.unlimited
            ? t('publishing.ai.usageUnlimited', '{{used}} used this month', { used: usage.used || 0 })
            : t('publishing.ai.usage', '{{used}} of {{limit}} this month', { used: usage.used || 0, limit: usage.limit })}
        </span>
      </button>

      {open && (
        <div className="pb-ai-body">
          <label className="pb-field" htmlFor="pb-ai-brief">
            {t('publishing.ai.brief', 'What is the post about? (for a new draft)')}
            <input
              id="pb-ai-brief"
              type="text"
              maxLength={1000}
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder={t('publishing.ai.briefPlaceholder', 'e.g. Volunteers needed for Saturday’s park cleanup, 9 AM, gloves provided')}
            />
          </label>
          <div className="pb-ai-row">
            <label className="pb-field" htmlFor="pb-ai-platform">
              {t('publishing.ai.for', 'Write for')}
              <select id="pb-ai-platform" value={platform} onChange={(e) => setTarget(e.target.value)}>
                {!known.length && <option value="">{t('publishing.ai.anyPlatform', 'Any platform')}</option>}
                {known.map((p) => <option key={p} value={p}>{NAMES[p]}</option>)}
              </select>
            </label>
            <label className="pb-field" htmlFor="pb-ai-tone">
              {t('publishing.ai.tone', 'Tone')}
              <select id="pb-ai-tone" value={tone} onChange={(e) => setTone(e.target.value)}>
                <option value="friendly">{t('publishing.ai.toneFriendly', 'Friendly')}</option>
                <option value="excited">{t('publishing.ai.toneExcited', 'Excited')}</option>
                <option value="formal">{t('publishing.ai.toneFormal', 'Formal')}</option>
                <option value="urgent">{t('publishing.ai.toneUrgent', 'Urgent')}</option>
              </select>
            </label>
          </div>
          <div className="pb-actions">
            <button type="button" className="pb-btn" disabled={!!busy || outOfAllowance || (!brief.trim() && !text?.trim() && !eventId)} onClick={() => run('draft')}>
              {busy === 'draft' ? t('publishing.ai.working', 'Writing…') : t('publishing.ai.draft', 'Draft')}
            </button>
            <button type="button" className="pb-btn" disabled={!!busy || outOfAllowance || !text?.trim()} onClick={() => run('shorten')}>
              {busy === 'shorten' ? t('publishing.ai.working', 'Writing…') : t('publishing.ai.shorten', 'Shorten')}
            </button>
            <button type="button" className="pb-btn" disabled={!!busy || outOfAllowance || !text?.trim() || !platform} onClick={() => run('adapt')}>
              {busy === 'adapt' ? t('publishing.ai.working', 'Writing…') : t('publishing.ai.adapt', 'Adapt for {{platform}}', { platform: NAMES[platform] || '' })}
            </button>
            <button type="button" className="pb-btn" disabled={!!busy || outOfAllowance || !text?.trim()} onClick={() => run('hashtags')}>
              {busy === 'hashtags' ? t('publishing.ai.working', 'Writing…') : t('publishing.ai.hashtags', 'Suggest hashtags')}
            </button>
          </div>

          {outOfAllowance && (
            <div className="note warn" role="status">
              {t('publishing.ai.allowanceUsed', 'You have used this month’s AI captions.')}{' '}
              <Link to={`/${tenantId}/billing`}>{t('publishing.ai.upgrade', 'See plans in Billing')}</Link>
            </div>
          )}
          {error && (
            <div className="note error" role="alert">
              {error.message}
              {error.code === 'PLAN_LIMIT_REACHED' && <> <Link to={`/${tenantId}/billing`}>{t('publishing.ai.upgrade', 'See plans in Billing')}</Link></>}
            </div>
          )}

          {result && result.text !== undefined && (
            <div className="pb-ai-result" data-testid="ai-result">
              <p className="pb-ai-text">{result.text}</p>
              <div className="pb-ai-meta">{t('publishing.ai.chars', '{{count}} characters', { count: result.text.length })}</div>
              <div className="pb-actions">
                <button type="button" className="pb-btn primary" onClick={() => { onChangeText(result.text); setResult(null); }}>
                  {t('publishing.ai.use', 'Use this')}
                </button>
                <button type="button" className="pb-btn" onClick={() => setResult(null)}>{t('publishing.ai.discard', 'Discard')}</button>
              </div>
            </div>
          )}
          {result && result.hashtags && (
            <div className="pb-ai-result" data-testid="ai-result">
              <p className="pb-ai-meta">{t('publishing.ai.tapToAdd', 'Tap a hashtag to add it to the post.')}</p>
              <div className="pb-chips">
                {result.hashtags.map((tag) => (
                  <button key={tag} type="button" className={`pb-chip${(text || '').includes(tag) ? ' is-on' : ''}`} onClick={() => addTag(tag)}>{tag}</button>
                ))}
              </div>
            </div>
          )}
          <p className="pb-ai-note">{t('publishing.ai.note', 'AI suggestions can be wrong. Check names, dates and prices before you post.')}</p>
        </div>
      )}
    </div>
  );
}
